// portalBridge.ts — the planet's half of the portal to/from the flat isometric map
// (dseek.ai/data/life/isometric). The map hands context over in the URL (?lang/?scenario/
// ?persona/?from); this reads + validates it, and builds the return URL that carries the
// planet's context back so a map→planet→map round-trip preserves language, weather + persona.

const ISO_BASE = 'https://dseek.ai/data/life/isometric/';

export interface MapContext {
  lang: string | null; // ui language carried from the map (en|tc|ko|ja|th)
  scenario: string | null; // live weather/time (day|golden|night|rain|typhoon) → planet atmosphere
  persona: string | null; // the map's walking persona (round-tripped)
  from: string | null; // 'map' → arrived through the portal (play the entry veil)
  /** dseek's `?news=1` — "open the spot's news when you land" (portal.js / newsGeoFeed.ts send it). */
  news: boolean;
}

// `blackout` is not the map's weather but the sea's world pulse (sea/pulse): the port the
// captain docked in has its lamps out, and the sea hands that over on the same param.
export const PORTAL_SCENARIOS = new Set(['day', 'golden', 'night', 'rain', 'typhoon', 'undersea', 'blackout']);
const PORTAL_LANGS = new Set(['en', 'tc', 'ko', 'ja', 'th']);

/** Read + validate the context the flat map carried across. Unknown lang/scenario values are
 *  dropped (null) so a typo or hostile query can never drive the scene into a bad state. */
export function readMapContext(search = typeof window !== 'undefined' ? window.location.search : ''): MapContext {
  const p = new URLSearchParams(search);
  const clean = (k: string, set: Set<string>): string | null => {
    const v = p.get(k);
    return v && set.has(v) ? v : null;
  };
  return {
    lang: clean('lang', PORTAL_LANGS),
    scenario: clean('scenario', PORTAL_SCENARIOS),
    persona: p.get('persona'),
    from: p.get('from'),
    news: p.get('news') === '1' || p.get('news') === 'true',
  };
}

/** Bounding window for the reverse pin deep-link — a hostile or garbage ?pin=
 *  can never point the scene at ground off the Hong Kong territory. */
const PIN_LAT_MIN = 22.1;
const PIN_LAT_MAX = 22.65;
const PIN_LON_MIN = 113.8;
const PIN_LON_MAX = 114.5;

/** Read the reverse pin deep-link `?pin=lat,lon` — the flat map (dseek) carrying a
 *  pinned spot INTO the planet, the mirror of the lat/lon `buildIsoUrl` sends back.
 *  Null unless both halves parse to finite coordinates inside Hong Kong, same
 *  drop-don't-guess discipline as `readMapContext`. */
export function readPinParam(
  search = typeof window !== 'undefined' ? window.location.search : '',
): { lat: number; lon: number } | null {
  const raw = new URLSearchParams(search).get('pin');
  if (!raw) return null;
  const parts = raw.split(',');
  if (parts.length !== 2) return null;
  const lat = Number(parts[0]);
  const lon = Number(parts[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat < PIN_LAT_MIN || lat > PIN_LAT_MAX || lon < PIN_LON_MIN || lon > PIN_LON_MAX) return null;
  return { lat, lon };
}

/** A completed Courier Rush outcome carried back to the map (ADR-0228) — the map
 *  shows it as a small toast/stamp. Compact URL params only; no cross-origin storage. */
export interface RunHandoff {
  stars: 0 | 1 | 2 | 3;
  score: number;
}

/** Build the return URL to the flat map for a district's DZI, carrying context back. `from=planet`
 *  tells the map to play its entry veil; en/day are the defaults so they carry nothing. A finished
 *  run's stars/score ride along (validated here so a malformed result is dropped, not shipped). */
export function buildIsoUrl(
  dzi: string | undefined,
  ctx: Pick<MapContext, 'lang' | 'scenario' | 'persona'>,
  result?: RunHandoff,
  pin?: { lat: number; lon: number },
): string {
  const q = new URLSearchParams();
  if (dzi) q.set('dzi', dzi);
  q.set('from', 'planet');
  if (ctx.lang && ctx.lang !== 'en') q.set('lang', ctx.lang);
  if (ctx.scenario && ctx.scenario !== 'day') q.set('scenario', ctx.scenario);
  if (ctx.persona) q.set('persona', ctx.persona);
  // Pin-to-scan: a tapped spot rides as lat/lon — the map flies there and drops
  // its own pin (its lens-switch reads these; its nearest-landmark resolution is
  // map-side, so we NEVER send its `landmark` ids — the id spaces differ).
  if (pin && Number.isFinite(pin.lat) && Number.isFinite(pin.lon)) {
    q.set('lat', pin.lat.toFixed(6));
    q.set('lon', pin.lon.toFixed(6));
  }
  if (
    result &&
    Number.isInteger(result.stars) &&
    result.stars >= 0 &&
    result.stars <= 3 &&
    Number.isFinite(result.score) &&
    result.score >= 0
  ) {
    q.set('stars', String(result.stars));
    q.set('score', String(Math.round(result.score)));
  }
  return `${ISO_BASE}?${q.toString()}`;
}

/** The flat map's story view for one of dseek's landmark ids. `?landmark=` is dseek's
 *  OWN id space — the planet's `lm-*` ids never cross (ADR-0228); the planet-private
 *  mapping lives in scan/landmarkSpots.ts. */
export function buildIsoStoryUrl(storyId: string, ctx: Pick<MapContext, 'lang'>): string {
  const q = new URLSearchParams();
  q.set('landmark', storyId);
  q.set('from', 'planet');
  if (ctx.lang && ctx.lang !== 'en') q.set('lang', ctx.lang);
  return `${ISO_BASE}?${q.toString()}`;
}
